import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Button } from './Button';
import { getErrorMessage } from '../utils/errorMessages';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmDelete: (reason: string) => Promise<{ success: boolean; error?: string }>;
  userRole?: 'service_user' | 'provider';
}

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  onConfirmDelete,
  userRole = 'service_user',
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDelete = async () => {
    if (!reason.trim()) {
      setDeleteError('Please provide a reason for account deletion');
      return;
    }

    if (reason.trim().length < 10) {
      setDeleteError('Reason must be at least 10 characters long');
      return;
    }

    if (confirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    const result = await onConfirmDelete(reason);

    if (!result.success) {
      setIsDeleting(false);
      setDeleteError(getErrorMessage(result.error, 'Failed to submit deletion request. Please try again.'));
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
      setConfirmText('');
      setReason('');
      setDeleteError('');
    }
  };

  const isProvider = userRole === 'provider';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Delete Account">
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900 mb-1">Warning: This action is permanent</p>
            <p className="text-sm text-red-800 mb-2">
              Your account and all associated data will be permanently deleted. This action
              cannot be undone.
            </p>
            {isProvider && (
              <ul className="text-sm text-red-800 list-disc list-inside space-y-1 mt-2">
                <li>Your CQC verification status will be removed</li>
                <li>All client relationships will be terminated</li>
                <li>Business data and service history will be lost</li>
              </ul>
            )}
            {!isProvider && (
              <ul className="text-sm text-red-800 list-disc list-inside space-y-1 mt-2">
                <li>All bookings and appointments will be cancelled</li>
                <li>Your care preferences will be lost</li>
                <li>Provider relationships will be terminated</li>
              </ul>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for Deletion <span className="text-red-600">*</span>
          </label>
          <p className="text-sm text-gray-600 mb-2">
            Please tell us why you want to delete your account (minimum 10 characters)
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="E.g., Not Interested, Found Alternative Service, etc."
            disabled={isDeleting}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
          />
          <div className="flex justify-between mt-1">
            <p className="text-xs text-gray-500">
              {reason.length}/500 characters
            </p>
            {reason.trim().length > 0 && reason.trim().length < 10 && (
              <p className="text-xs text-red-600">
                At least 10 characters required
              </p>
            )}
          </div>
        </div>

        <div>
          <p className="text-sm text-gray-700 mb-2">
            To confirm, please type <span className="font-bold">DELETE</span> in the box below:
          </p>
          <Input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            disabled={isDeleting}
          />
        </div>

        {deleteError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{deleteError}</p>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDelete}
            disabled={isDeleting || confirmText !== 'DELETE' || !reason.trim() || reason.trim().length < 10}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? 'Submitting Request...' : 'Delete My Account'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
